import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import React from 'react';

import { CellAlignment, Column } from '@/components/table/types';
import { Tooltip } from '@/components/Tooltip';
import { NOTIFICATION_TABLE_COLUMN_WIDTHS } from '@/sections/settings/components/cards/notifications/constants';
import { SeverityThresholdCell } from '@/sections/settings/components/cells/SeverityThresholdCell';
import { CopyIconButton } from '@/sections/settings/components/primitives/CopyIconButton';
import { RowActionsMenu } from '@/sections/settings/components/primitives/RowActionsMenu';
import { Account } from '@/types';

interface EmailColumnsConfig {
  /** Handler for viewing email configuration (severity-only editing) */
  onView: (email: Account) => void;
  /** Handler for deleting an email notification */
  onDelete: (email: Account) => void;
}

/**
 * Column definitions for Email Notifications TanStackTable.
 *
 * Provides:
 * - Email address column (expandable)
 * - Actions column with ellipsis menu (Edit, Delete)
 */
export function getEmailColumns({
  onView,
  onDelete,
}: EmailColumnsConfig): Column<Account>[] {
  return [
    {
      id: 'value',
      label: 'Email Address',
      fixedWidth: NOTIFICATION_TABLE_COLUMN_WIDTHS.primaryValue,
      cell: item => (
        <div className="flex items-center gap-2">
          <span className="text-chariot-text-primary font-mono text-sm">
            {item.value || 'Email notification configured'}
          </span>
          {item.value && (
            <CopyIconButton
              value={item.value}
              label="Email"
              data-testid={`copy-email-${item.key}`}
            />
          )}
        </div>
      ),
    },
    {
      id: 'severities' as keyof Account,
      label: (
        <span className="flex items-center gap-1">
          Severity Threshold
          <Tooltip title="Minimum vulnerability severity level that will trigger email notifications">
            <InformationCircleIcon className="text-chariot-text-secondary size-4 shrink-0 cursor-pointer" />
          </Tooltip>
        </span>
      ),
      fixedWidth: NOTIFICATION_TABLE_COLUMN_WIDTHS.severities,
      cell: item => {
        const threshold =
          (item.config?.severities as string) ||
          item.settings?.severityThreshold ||
          'MHC';
        return (
          <SeverityThresholdCell
            threshold={threshold}
            data-testid={`severity-${item.key}`}
          />
        );
      },
    },
    {
      id: '' as keyof Account,
      label: '',
      fixedWidth: NOTIFICATION_TABLE_COLUMN_WIDTHS.actions,
      align: 'right' as CellAlignment,
      cell: item => (
        <div className="flex justify-end">
          <RowActionsMenu
            testIdPrefix="email"
            itemKey={item.key}
            actions={[
              {
                label: 'Edit',
                icon: <PencilIcon className="size-4" />,
                onClick: () => onView(item),
              },
              {
                label: 'Delete',
                icon: <TrashIcon className="size-4" />,
                onClick: () => onDelete(item),
                variant: 'danger',
              },
            ]}
          />
        </div>
      ),
    },
  ];
}
